"use client"

import { Head, useForm, usePage } from "@inertiajs/react"
import { Send01 } from "@untitledui/icons"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { RadioButton, RadioGroup } from "@/components/ui/radio-buttons"
import { TextEditor } from "@/components/ui/text-editor"
import { DashboardLayout } from "@/layouts/dashboard-layout"
import { adminWarmStartsPath } from "@/routes"

import { type SeedRow, SeedRows, newSeedRow } from "./_components/seed-rows"

interface ComposeForm {
  warm_start: {
    channel: string
    recipient: { name: string; email: string }
    target: {
      kind: string
      title: string
      description: string
      rows: SeedRow[]
    }
    field_notes: { title: string; description: string }
  }
}

export default function NewWarmStart() {
  // CreateService refusals + validation failures arrive under the shared
  // `new_invite.*` keys the create action emits (same as the index modal);
  // the form has no per-field server errors (it raises, not validates).
  const { errors } = usePage<{ errors?: Record<string, string> }>().props
  const { data, setData, post, processing } = useForm<ComposeForm>({
    warm_start: {
      channel: "link",
      recipient: { name: "", email: "" },
      target: {
        kind: "playlist",
        title: "",
        description: "",
        rows: [newSeedRow()],
      },
      field_notes: { title: "", description: "" },
    },
  })

  const ws = data.warm_start
  const patchWs = (patch: Partial<ComposeForm["warm_start"]>) =>
    setData("warm_start", { ...ws, ...patch })
  const patchRecipient = (
    patch: Partial<ComposeForm["warm_start"]["recipient"]>,
  ) => patchWs({ recipient: { ...ws.recipient, ...patch } })
  const patchTarget = (patch: Partial<ComposeForm["warm_start"]["target"]>) =>
    patchWs({ target: { ...ws.target, ...patch } })
  const patchFieldNotes = (
    patch: Partial<ComposeForm["warm_start"]["field_notes"]>,
  ) => patchWs({ field_notes: { ...ws.field_notes, ...patch } })

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    post(adminWarmStartsPath())
  }

  const baseError =
    errors?.["new_invite.base"] ?? errors?.["new_invite.recipient_email"]

  return (
    <DashboardLayout>
      <Head title="New warm start" />
      <main className="mx-auto flex w-full max-w-2xl flex-col gap-8 px-4 pt-8 pb-12 lg:px-8">
        <header className="flex flex-col gap-0.5">
          <h1 className="text-primary md:text-display-xs text-xl font-semibold">
            New warm start
          </h1>
          <p className="text-md text-tertiary">
            Turn a conversation into a published artifact. You provision the
            account and a pre-filled draft; they click, tweak one thing, and
            publish.
          </p>
        </header>

        {baseError && (
          <div className="border-error bg-error-primary text-error-primary rounded-lg border px-4 py-3 text-sm">
            {baseError}
          </div>
        )}

        <form onSubmit={submit} className="flex flex-col gap-8">
          <Section title="Who's it for?">
            <Input
              isRequired
              label="Their name"
              placeholder="Maya"
              value={ws.recipient.name}
              onChange={(value) => patchRecipient({ name: value })}
            />
            <Input
              isRequired
              type="email"
              label="Their email"
              placeholder="maya@example.com"
              value={ws.recipient.email}
              onChange={(value) => patchRecipient({ email: value })}
            />
          </Section>

          <Section title="What are you seeding?">
            <RadioGroup
              aria-label="Artifact kind"
              value={ws.target.kind}
              onChange={(value) => patchTarget({ kind: value })}
              className="flex flex-col gap-3"
            >
              <RadioButton
                value="playlist"
                label="Playlist"
                hint="A set of tracks they curate and publish under their name."
              />
              <RadioButton
                value="brief"
                label="Brief contribution"
                hint="Coming soon."
                isDisabled
              />
            </RadioGroup>
          </Section>

          <Section title="The playlist">
            <Input
              isRequired
              label="Title"
              placeholder="Songs for the late shift"
              value={ws.target.title}
              onChange={(value) => patchTarget({ title: value })}
            />
            <Input
              label="Margin note for the title"
              hint="Private — nudges them to make the title theirs. Disappears once they change it."
              placeholder="e.g. rename this to something only you'd say"
              value={ws.field_notes.title}
              onChange={(value) => patchFieldNotes({ title: value })}
            />

            <TextEditor.Root
              content=""
              onUpdate={({ editor }) =>
                patchTarget({ description: editor.getHTML() })
              }
              placeholder="A line on what this playlist is about. (optional)"
              inputClassName="min-h-30 resize-y"
            >
              <TextEditor.Label>Description</TextEditor.Label>
              <TextEditor.Toolbar type="simple" />
              <TextEditor.Content />
            </TextEditor.Root>
            <Input
              label="Margin note for the description"
              hint="Private — only your invitee sees this."
              placeholder="e.g. add a sentence about why these got you through"
              value={ws.field_notes.description}
              onChange={(value) => patchFieldNotes({ description: value })}
            />
          </Section>

          <Section title="The tracks">
            <SeedRows
              rows={ws.target.rows}
              onChange={(rows) => patchTarget({ rows })}
            />
          </Section>

          <Section title="Delivery">
            <RadioGroup
              aria-label="Delivery"
              value={ws.channel}
              onChange={(value) => patchWs({ channel: value })}
              className="flex flex-col gap-3"
            >
              <RadioButton
                value="link"
                label="Copy a link"
                hint="You'll get a shareable magic link to send them yourself."
              />
              <RadioButton
                value="email"
                label="Email it for me"
                hint="Coming soon."
                isDisabled
              />
            </RadioGroup>
          </Section>

          <div className="flex justify-end">
            <Button
              type="submit"
              size="lg"
              iconLeading={Send01}
              isDisabled={processing}
              isLoading={processing}
              showTextWhileLoading
            >
              Create warm start
            </Button>
          </div>
        </form>
      </main>
    </DashboardLayout>
  )
}

function Section({
  title,
  children,
}: {
  title: string
  children: React.ReactNode
}) {
  return (
    <section className="flex flex-col gap-4">
      <h2 className="text-primary text-lg font-semibold">{title}</h2>
      {children}
    </section>
  )
}
